import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { categories } from '../../config/categories';
import './CategoryPage.css';
import ShopNav from '../../components/shopNav/ShopNav';
import Footer from '../../components/footer/footer';
import NavCategorias from '../../components/navbar/NavCategorias';
import ProductFilter from '../../components/filters/ProductFilter';
import ServiceHighlights from '../../components/servicehighlights/ServiceHighlights';

import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';
import { setPreviousRoute } from '../../store/navigationSlice';
import { showShopToast } from '../../utils/shopToast';
import ProductImageGallery from '../../components/shop/ProductImageGallery';

// ✅ Variable de entorno
const API_URL = import.meta.env.VITE_API_URL;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 9;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: '',
    sort: ''
  });
  

  const wishlistItems = useSelector(state => state.wishlist.items || []);

  const isInWishlist = (id) => {
    return wishlistItems.some(item => item.id === id);
  };

  const formatCOP = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  


  const categoryInfo = useMemo(() => {
    for (const mainCategory of categories) {
      if (mainCategory.id === categoryId) {
        return { mainCategory: mainCategory.id, subcategory: null };
      }
      const subcategory = mainCategory.subcategories.find(sub => sub.id === categoryId);
      if (subcategory) {
        return { mainCategory: mainCategory.id, subcategory: subcategory.id };
      }
    }
    return null;
  }, [categoryId]);

  const categoryName = useMemo(() => categoryId.replace(/-/g, ' '), [categoryId]);

  useEffect(() => {
    dispatch(setPreviousRoute(window.location.pathname));
  }, [dispatch, categoryId]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!categoryInfo) return;
      setLoading(true);
      setError(null);
      try {
        const endpoint = categoryInfo.subcategory
          ? `${API_URL}/stock/${categoryInfo.mainCategory}/${categoryInfo.subcategory}`
          : `${API_URL}/stock/${categoryInfo.mainCategory}`;
        const response = await axios.get(endpoint);
        console.log("📦 Productos recibidos desde el backend:", response.data);
        setProducts(response.data);
      } catch (err) {
        setError('Error al cargar los productos. Intente nuevamente.');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categoryInfo]);

  const filteredProducts = useMemo(() => {
    let result = products.filter(product => {
      const nameMatch = product.name.toLowerCase().includes(filters.search.toLowerCase());
      const minPriceMatch = filters.minPrice === '' || product.priceCOP >= parseFloat(filters.minPrice);
      const maxPriceMatch = filters.maxPrice === '' || product.priceCOP <= parseFloat(filters.maxPrice);
      return nameMatch && minPriceMatch && maxPriceMatch;
    });

    // Ordenar según el filtro seleccionado
    if (filters.sort === "price-asc") {
      result = result.sort((a, b) => a.priceCOP - b.priceCOP);
    } else if (filters.sort === "price-desc") {
      result = result.sort((a, b) => b.priceCOP - a.priceCOP);
    } else if (filters.sort === "name-asc") {
      result = result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (filters.sort === "name-desc") {
      result = result.sort((a, b) => b.name.localeCompare(a.name));
    }

    return result;
  }, [products, filters]);
  

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);


  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    dispatch(addToCart(product));
    showShopToast(`${product.name} agregado al carrito`, 'success');
  };


  const handleProductDetails = (productId) => {
    navigate(`/producto/${productId}`);
  };

  if (!categoryInfo) {
    return <div className="category-page-state text-center">Categoría no encontrada</div>;
  }

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      setToastMessage(`${product.name} eliminado de la lista de deseos`);
    } else {
      dispatch(addToWishlist(product));
      setToastMessage(`${product.name} agregado a la lista de deseos`);
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };


  return (
    <div className="category-page">
      <ShopNav />
      <NavCategorias />

      <main className="category-page-main">
        <section className="category-page-hero category-page-elevated-wrap category-page-elevated-wrap--hero">
          <div className="container category-page-hero-inner">
            <div className="category-page-hero-copy">
              <h1 className="display-5 animate__animated animate__fadeIn">{categoryName}</h1>
              <p className="lead animate__animated animate__fadeIn">
                Descubre productos seleccionados para esta categoría con calidad profesional para estudio, escenario y producción.
              </p>
            </div>
            <div className="category-page-hero-logo-wrap animate__animated animate__fadeIn" aria-hidden="true">
              <img src="/images/VPA.png" alt="Vintage Pro Audio" className="category-page-hero-logo" />
            </div>
          </div>
        </section>

        <section className="category-page-elevated-wrap">
          <ServiceHighlights />
        </section>

        <section className="category-page-elevated-wrap category-page-filter-wrap">
          <ProductFilter filters={filters} setFilters={setFilters} />
        </section>

        <section className="container category-page-results">
          {loading ? (
            <div className="category-page-state text-center">Cargando productos...</div>
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : (
            <>
              <div className="category-page-results-head">
                <h2 className="category-page-results-title">Productos de {categoryName}</h2>
                <p className="category-page-results-meta">{filteredProducts.length} producto(s) disponible(s)</p>
              </div>

              <div className="row g-4 category-products-grid">
                {currentProducts.map(product => {
                  let pdfs = {};

                  if (product.pdfUrl) {
                    try {
                      pdfs = JSON.parse(product.pdfUrl) || {};
                    } catch (parseError) {
                      console.error('Error parseando pdfUrl:', parseError);
                      pdfs = {};
                    }
                  }

                  return (
                    <div
                      className="col-12 col-sm-6 col-lg-4 category-product-col"
                      key={product.id}
                      onClick={() => handleProductDetails(product.id)}
                    >
                      <div className="card shadow-sm h-100 category-product-card">
                        <div className="card-body d-flex flex-column">
                          <div
                            className="category-card-wishlist"
                            onClick={(e) => handleToggleWishlist(e, product)}
                            title={isInWishlist(product.id) ? 'Eliminar de la lista de deseos' : 'Agregar a la lista de deseos'}
                            style={{ zIndex: 10 }}
                          >
                            <i
                              className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`}
                              style={{ fontSize: '1.5rem', cursor: 'pointer' }}
                            ></i>
                          </div>

                          <div onClick={(e) => e.stopPropagation()} className="image-gallery-wrapper category-card-media mb-3">
                            <ProductImageGallery
                              imageUrl={product.imageUrl}
                              productName={product.name}
                              showPlayButton={false}
                              showFullscreenButton={false}
                              showThumbnails={false}
                              slideInterval={3000}
                              autoPlay
                            />
                          </div>

                          <div className="category-card-meta">
                            <span className="category-card-chip">{categoryName}</span>
                            <span className="category-card-sku">Ref. {product.id}</span>
                          </div>

                          <h5 className="card-title">{product.name}</h5>
                          <p className="card-text">{product.description}</p>

                          <div className="category-card-pricing">
                            <p className="fw-bold text-success">{formatCOP(product.priceCOP)}</p>
                          </div>

                          <div className="category-card-actions mt-auto" onClick={(e) => e.stopPropagation()}>
                            {pdfs.pdf1 && (
                              <a
                                href={pdfs.pdf1}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary w-100"
                              >
                                Descargar Especificaciones
                              </a>
                            )}

                            <button
                              className="btn btn-success w-100"
                              onClick={(e) => handleAddToCart(e, product)}
                            >
                              Agregar al carrito
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <nav className="mt-4">
                <ul className="pagination justify-content-center category-pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(1)}>&laquo;</button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage - 1)}>&lsaquo;</button>
                  </li>
                  {[...Array(totalPages).keys()].map(number => (
                    <li key={number + 1} className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}>
                      <button onClick={() => paginate(number + 1)} className="page-link">
                        {number + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage + 1)}>&rsaquo;</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(totalPages)}>&raquo;</button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </section>

        <div
          className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container"
          style={{ zIndex: 9999 }}
        >

          <div
            className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">
                {toastMessage}
              </div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setShowToast(false)}
              ></button>
            </div>
          </div>
        </div>

      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;
